import { useState, useEffect } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'https://web-production-120c.up.railway.app';

function adminApi(path, options = {}) {
  const TOKEN = localStorage.getItem('shaheen_admin_token') || '';
  const KEY   = localStorage.getItem('shaheen_admin_key')   || '';
  return fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(TOKEN ? { 'Authorization': `Bearer ${TOKEN}` } : {}),
      'X-Shaheen-Key': KEY,
      ...options.headers,
    },
    ...options,
  }).then(r => r.json());
}

const s = {
  page: { padding: 24, overflowY: 'auto', flex: 1 },
  title: { fontSize: 18, fontWeight: 500, marginBottom: 20 },
  card: { background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, marginBottom: 12, overflow: 'hidden' },
  cardHeader: { padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderBottom: '0.5px solid var(--border)' },
  cardName: { fontSize: 14, fontWeight: 500, color: 'var(--text)' },
  cardMeta: { fontSize: 12, color: 'var(--text2)', marginTop: 2 },
  badge: { fontSize: 11, padding: '3px 10px', borderRadius: 7 },
  cardBody: { padding: '16px 18px' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 },
  field: { fontSize: 12 },
  fieldLabel: { color: 'var(--text3)', fontSize: 10, letterSpacing: '0.06em', marginBottom: 2 },
  fieldValue: { color: 'var(--text)', fontWeight: 500 },
  actions: { display: 'flex', gap: 10, marginTop: 16, paddingTop: 16, borderTop: '0.5px solid var(--border)' },
  approveBtn: { flex: 1, background: 'var(--gold)', color: 'var(--bg)', border: 'none', borderRadius: 7, padding: '10px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  rejectBtn: { background: 'var(--red-bg)', color: 'var(--red)', border: '0.5px solid var(--red)', borderRadius: 7, padding: '10px 20px', fontSize: 13, cursor: 'pointer' },
  qrBox: { background: 'var(--raised)', border: '0.5px solid var(--border)', borderRadius: 8, padding: 16, textAlign: 'center', marginTop: 14 },
  empty: { textAlign: 'center', padding: 40, color: 'var(--text2)', fontSize: 13 },
  tierBadge: { fontSize: 10, padding: '2px 8px', borderRadius: 6, letterSpacing: '0.06em' },
  promoTag: { fontSize: 11, color: 'var(--amber)', background: 'var(--amber-bg)', border: '0.5px solid var(--amber)', borderRadius: 5, padding: '2px 8px', marginLeft: 6 },
};

const tierColors = {
  core:  { background: '#181810', color: '#807050' },
  elite: { background: '#101818', color: '#508070' },
  black: { background: 'var(--gold-dim)', color: 'var(--gold)' },
};

const statusColors = {
  pending:  { background: 'var(--amber-bg)', color: 'var(--amber)' },
  approved: { background: 'var(--green-bg)', color: 'var(--green)' },
  rejected: { background: 'var(--red-bg)', color: 'var(--red)' },
};

function fmt(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return ts.slice(0, 16); }
}

export default function Signups() {
  const [signups, setSignups] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [processing, setProcessing] = useState({});
  const [qrData, setQrData] = useState({});

  const load = async () => {
    try {
      const d = await adminApi('/admin/signups');
      setSignups(d.signups || {});
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setProcessing(p => ({ ...p, [id]: 'approving' }));
    try {
      const d = await adminApi(`/admin/signups/${id}/approve`, { method: 'POST' });
      if (d.qr_url) setQrData(q => ({ ...q, [id]: d }));
      await load();
    } catch (e) { alert(e.message); }
    finally { setProcessing(p => ({ ...p, [id]: null })); }
  };

  const reject = async (id) => {
    if (!window.confirm('Reject this application?')) return;
    setProcessing(p => ({ ...p, [id]: 'rejecting' }));
    try { await adminApi(`/admin/signups/${id}/reject`, { method: 'POST' }); await load(); }
    catch (e) { alert(e.message); }
    finally { setProcessing(p => ({ ...p, [id]: null })); }
  };

  const list = Object.values(signups).sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
  const pending = list.filter(s => s.status === 'pending');
  const others = list.filter(s => s.status !== 'pending');

  return (
    <div style={s.page}>
      <div style={s.title}>
        Signups
        {pending.length > 0 && <span style={{ fontSize: 13, color: 'var(--amber)', fontWeight: 400, marginLeft: 10 }}>{pending.length} pending review</span>}
      </div>

      {loading ? <div style={s.empty}>Loading...</div> : list.length === 0 ? (
        <div style={s.empty}>No signups yet — share your signup link to get started</div>
      ) : (
        <>
          {pending.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text3)', letterSpacing: '0.08em', marginBottom: 10 }}>PENDING REVIEW</div>
          )}
          {[...pending, ...others].map(signup => (
            <div key={signup.signup_id} style={s.card}>
              <div style={s.cardHeader} onClick={() => setExpanded(expanded === signup.signup_id ? null : signup.signup_id)}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={s.cardName}>{signup.name}</span>
                    <span style={{ ...s.tierBadge, ...tierColors[signup.tier] || {} }}>{signup.tier?.toUpperCase()}</span>
                    {signup.promo_code && <span style={s.promoTag}>{signup.promo_code}</span>}
                  </div>
                  <div style={s.cardMeta}>{signup.company} · {signup.role} · {fmt(signup.submitted_at)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ ...s.badge, ...statusColors[signup.status] || {} }}>{signup.status}</span>
                  <span style={{ color: 'var(--text3)', fontSize: 12 }}>{expanded === signup.signup_id ? '▲' : '▼'}</span>
                </div>
              </div>

              {expanded === signup.signup_id && (
                <div style={s.cardBody}>
                  <div style={s.grid2}>
                    <div style={s.field}><div style={s.fieldLabel}>EMAIL</div><div style={s.fieldValue}>{signup.email}</div></div>
                    <div style={s.field}><div style={s.fieldLabel}>SECTOR</div><div style={s.fieldValue}>{signup.sector}</div></div>
                    <div style={s.field}><div style={s.fieldLabel}>BILLING</div><div style={s.fieldValue}>{signup.billing} — {signup.tier}</div></div>
                    <div style={s.field}><div style={s.fieldLabel}>SIGNUP ID</div><div style={{ ...s.fieldValue, fontFamily: 'monospace', fontSize: 12 }}>{signup.signup_id}</div></div>
                  </div>

                  {signup.emails_to_monitor?.length > 0 && (
                    <div style={s.field}><div style={s.fieldLabel}>EMAILS TO MONITOR</div><div style={s.fieldValue}>{signup.emails_to_monitor.join(', ')}</div></div>
                  )}
                  {Object.keys(signup.social_usernames || {}).length > 0 && (
                    <div style={{ ...s.field, marginTop: 8 }}><div style={s.fieldLabel}>SOCIAL MEDIA</div>
                      <div style={s.fieldValue}>{Object.entries(signup.social_usernames).map(([k, v]) => `${k}: ${v}`).join(' · ')}</div>
                    </div>
                  )}
                  {signup.notes && (
                    <div style={{ ...s.field, marginTop: 8 }}><div style={s.fieldLabel}>NOTES</div><div style={{ ...s.fieldValue, fontWeight: 400, color: 'var(--text2)' }}>{signup.notes}</div></div>
                  )}
                  {signup.promo_discount && (
                    <div style={{ ...s.field, marginTop: 8 }}><div style={s.fieldLabel}>PROMO APPLIED</div><div style={{ color: 'var(--amber)' }}>{signup.promo_code} — {signup.promo_discount.description}</div></div>
                  )}

                  {qrData[signup.signup_id] && (
                    <div style={s.qrBox}>
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>Client approved — send this QR code to {signup.name}</div>
                      <img src={qrData[signup.signup_id].qr_url} alt="QR" style={{ width: 160, height: 160, border: '4px solid white', borderRadius: 6 }} />
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>Client ID: <strong style={{ color: 'var(--text)' }}>{qrData[signup.signup_id].client_id || signup.client_id}</strong></div>
                      <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text2)', marginTop: 4, wordBreak: 'break-all' }}>{qrData[signup.signup_id].qr_url}</div>
                    </div>
                  )}

                  {signup.status === 'pending' && (
                    <div style={s.actions}>
                      <button style={s.approveBtn} onClick={() => approve(signup.signup_id)} disabled={processing[signup.signup_id]}>
                        {processing[signup.signup_id] === 'approving' ? 'Approving...' : 'Approve & activate'}
                      </button>
                      <button style={s.rejectBtn} onClick={() => reject(signup.signup_id)} disabled={processing[signup.signup_id]}>
                        Reject
                      </button>
                    </div>
                  )}
                  {signup.status === 'approved' && !qrData[signup.signup_id] && (
                    <div style={{ marginTop: 14, fontSize: 12, color: 'var(--green)' }}>✓ Approved on {fmt(signup.approved_at)} — welcome email sent</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
